import * as React from "react"

import { Search } from "lucide-react"

import { cn } from "../../cn"
import { focusOnMount } from "../../focusOnMount"
import { Button } from "../Button/Button"
import { Input, type InputProps } from "../Input/Input"
import { Popover, PopoverContent, PopoverTrigger } from "../Popover/Popover"

export type CollapsibleSearchInputProps = Omit<
  InputProps,
  "type" | "startAdornment" | "endAdornment"
> & {
  onClear: () => void
  /**
   * Controls the collapse instead of the built-in `@container` query. `true`
   * forces the icon/popover form, `false` forces the inline input; leave
   * undefined to keep the self-collapsing `@min-[820px]` behavior. Used by
   * `ActionToolbar` to drive the collapse from its width measurement.
   */
  collapsed?: boolean
  "data-testid"?: string
}

/**
 * A search field for page headers that renders inline when there is room and
 * collapses to a search-icon button (opening the field in a popover) when the
 * header gets narrow.
 *
 * By default the collapse tracks the *container's* width via a `@container`
 * query, so it keeps working as the sidebar or docked panels shrink the
 * available space; it must then sit inside an element with the `@container`
 * class (typically the page header root) and the threshold is 820px of
 * container width. Pass `collapsed` to drive the collapse externally instead.
 *
 * The forwarded ref and any extra props (e.g. `data-testid`) are attached to
 * the inline input. Calling `.focus()` on the ref works in both layouts: when
 * the field has collapsed (so the inline input is `display: none` and can't
 * take focus) it opens the popover instead, whose input takes focus on mount. This keeps
 * the `/` search shortcut working at every width.
 */
const CollapsibleSearchInput = React.forwardRef<
  HTMLInputElement,
  CollapsibleSearchInputProps
>(
  (
    {
      className,
      onClear,
      placeholder,
      value,
      onChange,
      disabled,
      collapsed,
      "aria-label": ariaLabel = "Search",
      ...rest
    },
    forwardedRef,
  ) => {
    const inlineRef = React.useRef<HTMLInputElement>(null)
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
    const isControlled = collapsed !== undefined
    const inlineVisibilityClass = isControlled
      ? collapsed
        ? "hidden"
        : "flex"
      : "hidden @min-[820px]:flex"
    const triggerVisibilityClass = isControlled
      ? collapsed
        ? ""
        : "hidden"
      : "@min-[820px]:hidden"

    // Expose the inline input to consumers, but make `.focus()` resilient to
    // the collapsed layout: a `display: none` element ignores `focus()`, so
    // when the inline input is hidden we open the popover (whose input takes
    // focus on mount) rather than silently no-op the search shortcut. Every other
    // member transparently delegates to the real inline input.
    React.useImperativeHandle(forwardedRef, () => {
      const node = inlineRef.current
      if (!node) {
        return null as unknown as HTMLInputElement
      }

      return new Proxy(node, {
        get(target, prop) {
          if (prop === "focus") {
            return (options?: FocusOptions) => {
              if (disabled) {
                return
              }
              if (target.offsetParent === null) {
                setIsPopoverOpen(true)
              } else {
                target.focus(options)
              }
            }
          }

          // Delegate every other member to the real node. Native DOM methods
          // brand-check `this`, so bind them to the element rather than letting
          // them run against the proxy.
          const member = target[prop as keyof HTMLInputElement]
          return typeof member === "function" ? member.bind(target) : member
        },
      })
    }, [disabled])

    // The collapse is driven purely by CSS `@container` queries, so React has no
    // signal for it. When the header grows back to the wide layout the inline
    // input reappears while an already-open popover would keep floating — both
    // search surfaces visible at once. Watch the inline input becoming visible
    // (its `offsetParent` is non-null only in the wide layout) and close the
    // popover so the two layouts stay mutually exclusive.
    React.useEffect(() => {
      const node = inlineRef.current
      if (!node || typeof ResizeObserver === "undefined") {
        return
      }
      const observer = new ResizeObserver(() => {
        if (node.offsetParent !== null) {
          setIsPopoverOpen(false)
        }
      })
      observer.observe(node)
      return () => observer.disconnect()
    }, [])

    const shared = {
      type: "search" as const,
      startAdornment: <Search />,
      placeholder,
      value,
      onChange,
      onClear,
      disabled,
      "aria-label": ariaLabel,
    }

    return (
      <>
        {/* Wide: full inline search input */}
        <Input
          {...shared}
          {...rest}
          ref={inlineRef}
          className={cn("w-64", className, inlineVisibilityClass)}
        />
        {/* Narrow: collapse to a search icon that opens the input in a popover */}
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              icon
              disabled={disabled}
              tooltip={ariaLabel}
              className={triggerVisibilityClass}
            >
              <Search />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-2">
            <Input
              {...shared}
              ref={focusOnMount}
              className={cn("w-64", className)}
            />
          </PopoverContent>
        </Popover>
      </>
    )
  },
)

CollapsibleSearchInput.displayName = "CollapsibleSearchInput"

export { CollapsibleSearchInput }
